import { useCallback, useEffect, useRef, useState } from "react";
import useTableStore from "../../../store/useTabStore";
import { Backend } from "../../../url";
import { SearchType, SortConfig, TableData } from "../types";

interface UseTableDataProps {
  searchType: SearchType;
  searchText: string;
  currentPage: number;
  pageSize: number;
  sortConfig: SortConfig;
  isReadyToFetch: boolean;
}

const useTableData = ({
  searchType,
  searchText,
  currentPage,
  pageSize,
  sortConfig,
  isReadyToFetch,
}: UseTableDataProps) => {
  const [data, setData] = useState<TableData | null>(null);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const requestInProgress = useRef(false);

  const { structureSearchParams, activeFilters, hasActiveFilters } =
    useTableStore();

  const generateApiUrl = useCallback(() => {
    const urlSearchParams = new URLSearchParams();
    urlSearchParams.set("page", currentPage.toString());
    urlSearchParams.set("page_size", pageSize.toString());

    if (searchText) {
      urlSearchParams.set("search", searchText);
    }

    if (sortConfig.column) {
      const orderingValue = `${sortConfig.direction === "DESC" ? "-" : ""}${
        sortConfig.column
      }`;
      urlSearchParams.set("ordering", orderingValue);
    }

    if (searchType === "structure") {
      if (!structureSearchParams) return null;
      const { type, smiles, threshold } = structureSearchParams;
      urlSearchParams.set("type", type);
      urlSearchParams.set("smiles", smiles);
      if (type === "similarity" && threshold != null) {
        urlSearchParams.set("threshold", threshold.toString());
      }
      return `/search/molecule/?${urlSearchParams.toString()}`;
    } else {
      if (hasActiveFilters()) {
        const formatRangeValue = (value: any) => {
          if (
            typeof value === "object" &&
            value !== null &&
            "min" in value &&
            "max" in value
          ) {
            return {
              gte: value.min,
              lte: value.max,
            };
          }
          return value;
        };

        const filtersForApi = Object.entries(activeFilters).reduce(
          (acc, [key, value]) => {
            if (
              value !== null &&
              value !== undefined &&
              (typeof value !== "string" || value !== "")
            ) {
              acc[key] = formatRangeValue(value);
            }
            return acc;
          },
          {} as Record<string, any>
        );

        if (process.env.REACT_APP_IS_PROD === "true") {
          filtersForApi["status"] = "published";
        }

        if (Object.keys(filtersForApi).length > 0) {
          urlSearchParams.set("filter", JSON.stringify(filtersForApi));
        }
      } else if (process.env.REACT_APP_IS_PROD === "true") {
        urlSearchParams.set("filter", JSON.stringify({ status: "published" }));
      }
      return `/search/textsearch/?${urlSearchParams.toString()}`;
    }
  }, [
    currentPage,
    pageSize,
    searchText,
    sortConfig.column,
    sortConfig.direction,
    searchType,
    structureSearchParams,
    hasActiveFilters,
    activeFilters,
  ]);

  useEffect(() => {
    if (!isReadyToFetch || requestInProgress.current) {
      return;
    }

    const fetchData = async () => {
      const apiUrl = generateApiUrl();
      if (!apiUrl) {
        setData(null);
        setTotalPages(1);
        setLoading(false);
        return;
      }

      requestInProgress.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${Backend}${apiUrl}`);
        if (!response.ok) {
          let errorData;
          try {
            errorData = await response.json();
          } catch (e) {}
          const errorMessage =
            errorData?.detail || `HTTP 오류! 상태: ${response.status}`;
          throw new Error(errorMessage);
        }
        const responseData = await response.json();
        setData(responseData);
        setTotalPages(Math.ceil(responseData.count / pageSize) || 1);
      } catch (err) {
        console.error("데이터 가져오기 오류:", err);
        setError(
          err instanceof Error ? err.message : "알 수 없는 오류가 발생했습니다"
        );
        setData(null);
        setTotalPages(1);
      } finally {
        setLoading(false);
        requestInProgress.current = false;
      }
    };

    fetchData();
  }, [isReadyToFetch, pageSize, generateApiUrl]);

  return { data, loading, error, totalPages };
};

export default useTableData;
